import React, { useEffect, useState } from "react";
import { apiClient } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { ShieldCheck, Star, Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import CartalVerifiedPartnersStrip from "../components/CartalVerifiedPartnersStrip";

export default function InsurancePage() {
  const { t, lang } = useI18n();
  const [offers, setOffers] = useState([]);
  const [vehicleValue, setVehicleValue] = useState(150000);
  const [coverage, setCoverage] = useState("all");
  const [lead, setLead] = useState({ name: "", phone: "", email: "" });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { apiClient.get("/insurance/offers").then(({ data }) => setOffers(data.items || [])); }, []);

  const filtered = coverage === "all" ? offers : offers.filter((o) => o.coverage_type.toLowerCase().includes(coverage));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await apiClient.post("/insurance/leads", lead); setSubmitted(true); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="insurance-page">
      <div className="overline mb-2">INSURANCE MARKETPLACE</div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-medium">{lang === "ar" ? "قارن التأمين واشترِ خلال دقيقة" : "Compare quotes. Buy in 60 seconds."}</h1>
      <p className="text-[#6b6b6b] mt-2 max-w-2xl">{lang === "ar" ? "أكبر شركات التأمين في مكان واحد. مقارنة فورية، شراء آني، تجديد بنقرة." : "All major insurers in one place. Instant comparison, online purchase, one-tap renewal."}</p>

      {/* Phase 2F — CARTAL verified insurance partners (silent when none) */}
      <CartalVerifiedPartnersStrip
        categories={["insurance_company", "insurance_broker"]}
        ctaAr="شركات وسطاء التأمين الموثّقون في كرتال"
        ctaEn="Verified insurance partners on CARTAL"
      />

      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8 mt-10">
        <div className="surface rounded-3xl p-8 lg:sticky lg:top-24 lg:self-start">
          <div className="overline mb-2">QUICK QUOTE</div>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "قيمة المركبة (SAR)" : "Vehicle value (SAR)"}</label>
              <input type="number" className="input-dark mt-1" value={vehicleValue} onChange={(e) => setVehicleValue(Number(e.target.value))} data-testid="ins-value" />
            </div>
            <div>
              <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "نوع التغطية" : "Coverage"}</label>
              <select className="input-dark mt-1" value={coverage} onChange={(e) => setCoverage(e.target.value)} data-testid="ins-coverage">
                <option value="all">{lang === "ar" ? "الكل" : "All"}</option>
                <option value="comprehensive">{lang === "ar" ? "شامل" : "Comprehensive"}</option>
                <option value="third">{lang === "ar" ? "ضد الغير" : "Third Party"}</option>
              </select>
            </div>
          </div>
          <form onSubmit={submit} className="mt-8 space-y-3" data-testid="insurance-lead-form">
            <div className="overline">SAVE YOUR QUOTE</div>
            <input className="input-dark" placeholder={t("common.name")} required value={lead.name} onChange={(e) => setLead({...lead, name: e.target.value})} />
            <input className="input-dark" placeholder={t("common.phone")} required value={lead.phone} onChange={(e) => setLead({...lead, phone: e.target.value})} />
            <input className="input-dark" type="email" placeholder={t("common.email")} required value={lead.email} onChange={(e) => setLead({...lead, email: e.target.value})} />
            <button className="btn-primary w-full justify-center" disabled={busy || submitted} data-testid="insurance-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : submitted ? <CheckCircle2 className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
              {submitted ? (lang === "ar" ? "تم الإرسال" : "Saved") : (lang === "ar" ? "احفظ الاقتباس" : "Save quote")}
            </button>
          </form>
        </div>

        <div className="space-y-4">
          {filtered.map((o) => {
            const premium = Math.round(vehicleValue * (o.premium_rate_pct / 100));
            return (
              <div key={o.id} className="surface rounded-2xl p-6  flex flex-col md:flex-row gap-4 items-start md:items-center" data-testid={`ins-offer-${o.id}`}>
                <div className="w-12 h-12 rounded-xl bg-[#c9a55c]/15 border border-[#c9a55c]/40 flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-[#8a6f2e]" /></div>
                <div className="flex-1">
                  <div className="font-display text-lg">{lang === "ar" ? o.provider_ar : o.provider_en}</div>
                  <div className="text-xs text-[#6b6b6b]">{o.coverage_type}</div>
                  <div className="mt-2 flex gap-2 flex-wrap">
                    {o.roadside_assistance && <span className="pill text-[10px]">Roadside</span>}
                    {o.agency_repair && <span className="pill text-[10px]">Agency Repair</span>}
                    <span className="pill text-[10px] inline-flex items-center"><Star className="w-3 h-3 me-1 text-[#8a6f2e]" />{o.rating}</span>
                  </div>
                </div>
                <div className="text-end">
                  <div className="text-xs text-[#6b6b6b]">{lang === "ar" ? "القسط السنوي" : "Annual premium"}</div>
                  <div className="font-display text-2xl text-[#8a6f2e]">{premium.toLocaleString()} <span className="text-xs text-[#6b6b6b]">SAR</span></div>
                  <button className="btn-primary mt-3 text-xs">{lang === "ar" ? "اشترِ الآن" : "Buy now"}</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
